import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import './market.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function Home({
  categories,
  showAddProductForm,
  setShowAddProductForm,
  newProduct,
  setNewProduct,
  handleAddProduct,
  setActiveCategory,
  handleDeleteCategory,
  onCategoryAdded,
}) {
  const { user } = useSelector((state) => ({ ...state }));
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subcategoryInputs, setSubcategoryInputs] = useState(['']);
  const [showSubcategorySection, setShowSubcategorySection] = useState(false);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [activeCategory, setActiveCategoryLocal] = useState(null);

  // Fetch products for each category to display images
  useEffect(() => {
    const fetchCategoryProducts = async () => {
      const productsByCategory = {};
      const defaultCategories = ['cars', 'bikes', 'home-appliances', 'electronics', 'mobiles'];
      
      for (const categoryId of defaultCategories) {
        try {
          const response = await axios.get(`${API_URL}/api/products/category/${categoryId}`, {
            headers: getAuthHeaders()
          });
          if (response.data.products && response.data.products.length > 0) {
            // Get the first product with an image
            const productWithImage = response.data.products.find(product => 
              product.images && product.images.length > 0
            );
            if (productWithImage) {
              productsByCategory[categoryId] = productWithImage.images[0];
            }
          }
        } catch (error) {
          console.error(`Error fetching products for ${categoryId}:`, error);
        }
      }
      setCategoryProducts(productsByCategory);
    };

    fetchCategoryProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = user?.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // This is now handled by the parent component (market.js)
  // Categories are passed as props and automatically updated

  // Remove duplicate categories
  const removeDuplicateCategories = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/categories/remove-duplicates`);
      if (response.data.removedCount > 0) {
        console.log(`Removed ${response.data.removedCount} duplicate categories`);
        // Refresh categories after removing duplicates
        console.log('Categories updated after removing duplicates');
      }
    } catch (error) {
      console.error('Error removing duplicate categories:', error);
    }
  };

  // Call remove duplicates when component mounts
  useEffect(() => {
    removeDuplicateCategories();
  }, []);

  // Add new subcategory input field
  const addSubcategoryInput = () => {
    setSubcategoryInputs(prev => [...prev, '']);
  };

  // Remove subcategory input field
  const removeSubcategoryInput = (index) => {
    setSubcategoryInputs(prev => prev.filter((_, i) => i !== index));
  };

  // Update subcategory input value
  const updateSubcategoryInput = (index, value) => {
    setSubcategoryInputs(prev => prev.map((item, i) => i === index ? value : item));
  };

  // Add new category to database
  const handleAddNewCategory = async () => {
    if (!user || !user.token) {
      alert('You must be logged in to create categories.');
      return;
    }

    if (newCategoryInput.trim()) {
      // Check for duplicate category name
      const categoryExists = categories.some(cat => 
        cat.name.toLowerCase() === newCategoryInput.trim().toLowerCase()
      );
      
      if (categoryExists) {
        alert('Category already exists! Please choose a different name.');
        return;
      }

      // Check for duplicate subcategories within the new category
      const validSubcategories = subcategoryInputs.filter(sub => sub.trim());
      const duplicateSubcategories = validSubcategories.filter((sub, index) => 
        validSubcategories.findIndex(s => s.toLowerCase() === sub.toLowerCase()) !== index
      );
      
      if (duplicateSubcategories.length > 0) {
        alert('Duplicate subcategories found! Please remove duplicate subcategory names.');
        return;
      }

      setLoading(true);
      try {
        const response = await axios.post(`${API_URL}/api/categories/add`, {
          name: newCategoryInput.trim(),
          subcategories: validSubcategories.length > 0 ? validSubcategories : undefined
        }, {
          headers: getAuthHeaders()
        });
        
        // Set as current product category
        setNewProduct({ ...newProduct, category: response.data.category.value });
        
        // Reset form
        setNewCategoryInput('');
        setSubcategoryInputs(['']);
        setShowSubcategorySection(false);
        setShowNewCategoryInput(false);
        
        // Show success message
        alert('Category added successfully!');
        
        // Trigger parent to refetch categories
        if (onCategoryAdded) {
          onCategoryAdded();
        }
        console.log('Categories updated after creation');
      } catch (error) {
        console.error('Error adding category:', error);
        const errorMessage = error.response?.data?.error || 'Failed to add category';
        if (errorMessage.includes('already exists')) {
          alert('Category already exists! Please choose a different name.');
        } else if (errorMessage.includes('Authentication required')) {
          alert('You must be logged in to create categories.');
        } else {
          alert(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // Auto slide for product images
  React.useEffect(() => {
    if (newProduct.images && newProduct.images.length > 1) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prev) => 
          prev === newProduct.images.length - 1 ? 0 : prev + 1
        );
      }, 3000);
      return () => clearInterval(timer);
    }
  }, [newProduct.images]);

  // Use categories passed from parent component
  const allCategories = categories;

  // Default fallback images for categories without products
  const defaultImages = {
    'cars': 'https://akm-img-a-in.tosshub.com/businesstoday/images/story/202006/second_hand_car_660_090620050440.jpg',
    'bikes': 'https://static.newstrack.com/uploads/images/2023/03/image_800x_64216757dddc8.jpg',
    'home-appliances': 'https://images.jdmagicbox.com/quickquotes/images_main/second-hand-wooden-dining-table-2219988877-d7yuy3bd.jpg',
    'electronics': 'https://c8.alamy.com/comp/DNHHBX/a-collection-of-second-hand-film-cameras-for-sale-in-a-photographic-DNHHBX.jpg',
    'mobiles': 'https://5.imimg.com/data5/SELLER/Default/2022/11/XR/AA/RE/21445559/oneplus-premium-mobiles-available-at-techpoint-with-an-best-price--500x500.jpeg'
  };

  const getCategoryDisplayName = (categoryId) => {
    const names = {
      'cars': 'Cars',
      'bikes': 'Bikes', 
      'home-appliances': 'Home Appliances',
      'electronics': 'Electronics',
      'mobiles': 'Mobiles'
    };
    return names[categoryId] || categoryId;
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    
    if (name.includes('car') || name.includes('vehicle')) {
      return 'directions_car';
    } else if (name.includes('bike') || name.includes('motorcycle')) {
      return 'motorcycle';
    } else if (name.includes('mobile') || name.includes('phone')) {
      return 'smartphone';
    } else if (name.includes('electronic') || name.includes('gadget')) {
      return 'devices';
    } else if (name.includes('home') || name.includes('appliance')) {
      return 'home';
    } else if (name.includes('furniture') || name.includes('chair') || name.includes('table')) {
      return 'chair';
    } else if (name.includes('book') || name.includes('education')) {
      return 'book';
    } else if (name.includes('cloth') || name.includes('fashion')) {
      return 'checkroom';
    } else if (name.includes('sport')) {
      return 'sports_basketball';
    } else if (name.includes('beauty') || name.includes('cosmetic')) {
      return 'face';
    } else if (name.includes('food') || name.includes('kitchen')) {
      return 'restaurant';
    } else {
      return 'category';
    }
  };

  return (
    <div className="initial-view">
      {/* Header with Add Product Button */}


      {/* Mobile Category Navigation Bar */}
      <div className="mobile-category-nav" style={{
        display: 'none', // Hidden by default, shown via CSS on mobile
        overflowX: 'auto',
        gap: '12px',
        padding: '20px 15px',
        background: '#e8f5e8',
        borderTop: '1px solid #dee2e6',
        marginTop: '20px'
      }}>
        {/* All Categories from Database */}
        {allCategories.length > 0 ? (
          allCategories.map((category) => (
            <div
              key={category._id || category.id}
              style={{
                position: 'relative',
                display: 'inline-block'
              }}
            >
              <button
                className={`mobile-category-btn ${activeCategory === (category.value || category.id) ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategoryLocal(category.value || category.id);
                  setActiveCategory(category.value || category.id);
                }}
              >
                {category.name}
              </button>
              
              {/* Delete button for non-default categories in mobile view */}
              {!category.isDefault && user && user.id && category.createdBy === user.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCategory(category._id || category.id);
                  }}
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)',
                    zIndex: 10
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.2) rotate(90deg)';
                    e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1) rotate(0deg)';
                    e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.3)';
                  }}
                  title="Delete Category"
                >
                  ×
                </button>
              )}
            </div>
          ))
        ) : (
          // Fallback to default categories if no categories from database
          <>
            <button
              className={`mobile-category-btn ${activeCategory === 'electronics' ? 'active' : ''}`}
              onClick={() => {
                setActiveCategoryLocal('electronics');
                setActiveCategory('electronics');
              }}
            >
              Electronics
            </button>
            <button
              className={`mobile-category-btn ${activeCategory === 'home-appliances' ? 'active' : ''}`}
              onClick={() => {
                setActiveCategoryLocal('home-appliances');
                setActiveCategory('home-appliances');
              }}
            >
              Home Appliances
            </button>
            <button
              className={`mobile-category-btn ${activeCategory === 'bikes' ? 'active' : ''}`}
              onClick={() => {
                setActiveCategoryLocal('bikes');
                setActiveCategory('bikes');
              }}
            >
              Bikes
            </button>
            <button
              className={`mobile-category-btn ${activeCategory === 'cars' ? 'active' : ''}`}
              onClick={() => {
                setActiveCategoryLocal('cars');
                setActiveCategory('cars');
              }}
            >
              Cars
            </button>
          </>
        )}
        
        {/* Add New Category Button */}
        <button
          className="mobile-category-btn add-new"
          onClick={() => setShowNewCategoryInput(true)}
        >
          + Add New Category
        </button>

        {/* Clear Filter Button - Only show when a category is active */}
        {activeCategory && (
          <button
            className="mobile-category-btn clear"
            onClick={() => {
              setActiveCategoryLocal(null);
              setActiveCategory(null);
            }}
          >
            Clear
          </button>
        )}
      </div>

      <div className="category-section">
        <h2 style={{ 
          fontSize: '1.75rem',
          fontWeight: '700',
          color: 'var(--gray-900)',
          margin: '0 0 1.5rem 0',
          textAlign: 'center'
        }}>
          Browse Categories
        </h2>
        
        <div className="category-list">
        {/* All categories from database */}
        {allCategories.length > 0 ? (
          allCategories.map((category) => (
              <button
              key={category._id || category.id}
                className="category-button"
                onClick={() => {
                  setActiveCategory(category.value || category.id);
                }}
                style={{ 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '2px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textAlign: 'center',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                  minWidth: '100px',
                  minHeight: '80px',
                  justifyContent: 'center',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.transform = 'translateY(-4px) scale(1.02)';
                  e.target.style.boxShadow = '0 20px 25px -5px rgba(102, 126, 234, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)';
                  e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
                  e.target.style.color = '#374151';
                }}
              >
                <span className="material-icons notranslate" style={{
                  fontSize: '1.5rem',
                  color: 'inherit',
                  marginBottom: '0.25rem'
                }}>
                  {getCategoryIcon(category.name)}
                </span>
                
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'inherit',
                  margin: 0,
                  lineHeight: '1.2'
                }}>
                {category.name}
                </span>
                
                {/* Delete button for non-default categories */}
              {!category.isDefault && user && user.id && category.createdBy === user.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCategory(category._id || category.id);
                  }}
                  style={{
                    position: 'absolute',
                      top: '0.25rem',
                      right: '0.25rem',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.2) rotate(90deg)';
                      e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1) rotate(0deg)';
                      e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.3)';
                  }}
                  title="Delete Category"
                >
                  ×
                </button>
              )}
              </button>
          ))
        ) : (
            <div className="empty-state" style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '2rem',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '16px',
              border: '2px dashed #cbd5e1'
            }}>
              <div className="empty-state-icon" style={{fontSize: '3rem', marginBottom: '1rem'}}>📂</div>
              <h3 className="empty-state-title" style={{margin: '0 0 0.5rem 0', color: '#374151'}}>No categories yet!</h3>
              <p className="empty-state-description" style={{margin: 0, color: '#64748b'}}>
              Create your first category to get started with your marketplace.
            </p>
          </div>
        )}
        
          {/* Add New Category Button */}
          <button 
            className="category-button add-new-category"
          onClick={() => setShowNewCategoryInput(true)}
          style={{ 
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              border: '2px dashed #cbd5e1',
              borderRadius: '16px',
              padding: '1rem',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              textAlign: 'center',
            display: 'flex',
              flexDirection: 'column',
            alignItems: 'center',
              gap: '0.5rem',
              minWidth: '100px',
              minHeight: '80px',
              justifyContent: 'center',
              color: '#64748b',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.color = '#667eea';
              e.target.style.transform = 'translateY(-4px) scale(1.02)';
              e.target.style.boxShadow = '0 20px 25px -5px rgba(102, 126, 234, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)';
              e.target.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#cbd5e1';
              e.target.style.color = '#64748b';
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              e.target.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)';
            }}
          >
            <span className="material-icons notranslate" style={{
              fontSize: '1.5rem',
              color: 'inherit',
              marginBottom: '0.25rem'
            }}>
              add_circle
            </span>

            <span style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              margin: 0,
              lineHeight: '1.2'
            }}>
              Add New Category
            </span>
          </button>
        </div>
      </div>

      {showAddProductForm && (
        <div className="modal-overlay" style={{ 
          zIndex: 1001,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div className="modal-content" style={{ 
            maxWidth: '700px', 
            width: '90vw',
            maxHeight: '90vh', 
            overflowY: 'auto',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            animation: 'slideIn 0.4s ease-out',
            backdropFilter: 'blur(10px)',
            transform: 'translateY(0)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            padding: '2rem'
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              right: '-2px',
              bottom: '-2px',
              background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '22px',
              zIndex: -1,
              opacity: 0.1
            }}></div>
            
            <div className="modal-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
              paddingBottom: '1.5rem',
              borderBottom: '2px solid rgba(99, 102, 241, 0.1)',
              position: 'relative'
            }}>
              <h2 className="modal-header-title" style={{
                fontSize: '1.75rem',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                letterSpacing: '-0.025em'
              }}>
                <span className="material-icons notranslate" style={{ 
                  color: '#667eea',
                  fontSize: '2rem',
                  filter: 'drop-shadow(0 2px 4px rgba(102, 126, 234, 0.3))'
                }}>add_shopping_cart</span>
                Add New Product
              </h2>
              <button
                className="modal-close"
                onClick={() => setShowAddProductForm(false)}
                style={{
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  border: '2px solid rgba(99, 102, 241, 0.1)',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '0.75rem',
                  borderRadius: '12px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: '44px',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'scale(1.1) rotate(90deg)';
                  e.target.style.boxShadow = '0 8px 15px -3px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                  e.target.style.color = '#64748b';
                  e.target.style.transform = 'scale(1) rotate(0deg)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {/* Name and Category Row */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                <div className="form-group">
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#374151',
                    marginBottom: '0.75rem',
                    letterSpacing: '-0.025em'
                  }}>
                    <span className="material-icons notranslate" style={{ 
                      fontSize: '1.25rem', 
                      marginRight: '0.5rem',
                      color: '#667eea',
                      verticalAlign: 'middle'
                    }}>inventory</span>
                    Product Name
                  </label>
            <input
              placeholder="Name"
              value={newProduct.name}
              onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '1rem 1.25rem',
                      borderRadius: '16px',
                      border: '2px solid #e5e7eb',
                      fontSize: '1.1rem',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      color: '#374151',
                      fontWeight: '500'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  />
                </div>
                
                <div className="form-group">
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                    color: '#374151',
                    marginBottom: '0.75rem',
                    letterSpacing: '-0.025em'
                  }}>
                    <span className="material-icons notranslate" style={{ 
                      fontSize: '1.25rem', 
                      marginRight: '0.5rem',
                      color: '#667eea',
                      verticalAlign: 'middle'
                    }}>category</span>
                    Category
                  </label>
            <select
                value={newProduct.category}
                onChange={(e) => {
                  if (e.target.value === "add_new") {
                    setShowNewCategoryInput(true);
                    return;
                  }
                  setNewProduct({ ...newProduct, category: e.target.value, mobileSubCategory: '', subcategory: '' })
                }}
                    style={{
                      width: '100%',
                      padding: '1rem 1.25rem',
                      borderRadius: '16px',
                      border: '2px solid #e5e7eb',
                      fontSize: '1.1rem',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      color: '#374151',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                    }}
              >
                <option value="">Select Category</option>
                {allCategories.map(cat => (
                  <option key={cat._id || cat.id} value={cat.value || cat.id}>
                    {cat.name}
                  </option>
                ))}
                <option value="add_new" style={{color: '#15b80f', fontWeight: 'bold'}}>+ Add New Category</option>
              </select>
                </div>
              </div>
              {/* Subcategory Section */}
            {(() => {
              const selectedCategory = allCategories.find(cat => cat.value === newProduct.category || cat.id === newProduct.category);
              const hasSubcategories = selectedCategory?.subcategories && selectedCategory.subcategories.length > 0;
              
              if (hasSubcategories) {
                return (
                    <div className="form-group" style={{ marginBottom: '2rem' }}>
                    <label style={{ 
                      display: 'block', 
                        fontSize: '0.875rem',
                        fontWeight: '700',
                        color: '#374151',
                        marginBottom: '0.75rem',
                        letterSpacing: '-0.025em'
                      }}>
                        <span className="material-icons notranslate" style={{ 
                          fontSize: '1.25rem', 
                          marginRight: '0.5rem',
                          color: '#667eea',
                          verticalAlign: 'middle'
                        }}>subdirectory_arrow_right</span>
                      Subcategory (Optional)
                    </label>
                    <select
                      value={newProduct.subcategory || newProduct.mobileSubCategory || ''}
                      onChange={e => setNewProduct({ 
                        ...newProduct, 
                        subcategory: e.target.value,
                        mobileSubCategory: newProduct.category === 'mobiles' ? e.target.value : ''
                      })}
                      style={{ 
                        width: '100%', 
                          padding: '1rem 1.25rem',
                          borderRadius: '16px',
                          border: '2px solid #e5e7eb',
                          fontSize: '1.1rem',
                          boxSizing: 'border-box',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          color: '#374151',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                          e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      <option value="">Select Subcategory (Optional)</option>
                      {selectedCategory.subcategories.map(sub => (
                        <option key={sub.value || sub.name} value={sub.value || sub.name}>
                          {sub.name}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }
              
              if (newProduct.category === 'mobiles') {
                return (
                    <div className="form-group" style={{ marginBottom: '2rem' }}>
                    <label style={{ 
                      display: 'block', 
                        fontSize: '0.875rem',
                        fontWeight: '700',
                        color: '#374151',
                        marginBottom: '0.75rem',
                        letterSpacing: '-0.025em'
                      }}>
                        <span className="material-icons notranslate" style={{ 
                          fontSize: '1.25rem', 
                          marginRight: '0.5rem',
                          color: '#667eea',
                          verticalAlign: 'middle'
                        }}>smartphone</span>
                      Mobile Subcategory (Optional)
                    </label>
                    <select
                      value={newProduct.mobileSubCategory || ''}
                      onChange={e => setNewProduct({ ...newProduct, mobileSubCategory: e.target.value })}
                      style={{ 
                        width: '100%', 
                          padding: '1rem 1.25rem',
                          borderRadius: '16px',
                          border: '2px solid #e5e7eb',
                          fontSize: '1.1rem',
                          boxSizing: 'border-box',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          color: '#374151',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                          e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      <option value="">Select Mobile Subcategory (Optional)</option>
                      <option value="iphone">iPhone</option>
                      <option value="android">Android</option>
                    </select>
                  </div>
                );
              }
              
              return null;
            })()}

              {/* Image Upload Section */}
              <div className="form-group" style={{ marginBottom: '2rem' }}>
            <div style={{ 
                  marginBottom: '1rem',
                  padding: '1rem 1.25rem',
                  backgroundColor: (newProduct.images || []).length < 3 ? '#fef3c7' : (newProduct.images || []).length === 5 ? '#dbeafe' : '#d1fae5',
                  borderRadius: '16px',
                  border: `2px solid ${(newProduct.images || []).length < 3 ? '#fbbf24' : (newProduct.images || []).length === 5 ? '#3b82f6' : '#10b981'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <span className="material-icons notranslate" style={{ 
                    fontSize: '1.5rem',
                    color: (newProduct.images || []).length < 3 ? '#d97706' : (newProduct.images || []).length === 5 ? '#2563eb' : '#059669'
                  }}>camera_alt</span>
                  <div>
                    <div style={{ 
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: (newProduct.images || []).length < 3 ? '#92400e' : (newProduct.images || []).length === 5 ? '#1e40af' : '#065f46'
                    }}>
                      Images: {(newProduct.images || []).length}/5
            </div>
                    <div style={{ 
                      fontSize: '0.875rem',
                      color: (newProduct.images || []).length < 3 ? '#a16207' : (newProduct.images || []).length === 5 ? '#1d4ed8' : '#047857'
                    }}>
                      {(newProduct.images || []).length < 3 ? `Need ${3 - (newProduct.images || []).length} more` : 
                       (newProduct.images || []).length === 5 ? 'Maximum reached' : 'Looking good!'}
                    </div>
                  </div>
                </div>
                
                <div style={{ position: 'relative' }}>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={e => {
                const currentImages = newProduct.images || [];
                const files = Array.from(e.target.files);
                const totalImages = currentImages.length + files.length;
                
                if (totalImages > 5) {
                  alert('You can upload maximum 5 images per product');
                  return;
                }
                
                const readers = files.map(file => {
                  return new Promise(resolve => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(file);
                  });
                });
                Promise.all(readers).then(images => {
                  setNewProduct({
                    ...newProduct,
                    images: [...currentImages, ...images].slice(0, 5)
                  });
                });
              }}
                    style={{
                      position: 'absolute',
                      opacity: 0,
                      width: '100%',
                      height: '100%',
                      cursor: 'pointer',
                      zIndex: 2
                    }}
                  />
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      padding: '1rem 1.5rem',
                      borderRadius: '16px',
                      border: '2px dashed #cbd5e1',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                      color: '#64748b',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.75rem'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.color = '#667eea';
                      e.target.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#cbd5e1';
                      e.target.style.color = '#64748b';
                      e.target.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <span className="material-icons notranslate" style={{ fontSize: '1.25rem' }}>cloud_upload</span>
                    Choose Files
                  </button>
                </div>
                
            {newProduct.images && newProduct.images.length > 0 && (
              <div style={{ 
                    marginTop: '1.5rem',
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: '1rem'
              }}>
                {newProduct.images.map((img, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <button
                      onClick={() => {
                        const newImages = newProduct.images.filter((_, i) => i !== idx);
                        setNewProduct({ ...newProduct, images: newImages });
                      }}
                      style={{
                        position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                            fontSize: '1rem',
                        fontWeight: 'bold',
                            transition: 'all 0.2s ease',
                            zIndex: 1,
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                      }}
                    >
                      ×
                    </button>
                    <img 
                      src={img} 
                      alt={`Preview ${idx + 1}`} 
                      style={{ 
                            width: '100%', 
                        height: '120px', 
                            borderRadius: '12px', 
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        objectFit: 'cover',
                            border: '2px solid #e5e7eb'
                      }} 
                    />
                  </div>
                ))}
              </div>
            )}
              </div>

              {/* Price Section */}
              <div className="form-group" style={{ marginBottom: '2rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#374151',
                  marginBottom: '0.75rem',
                  letterSpacing: '-0.025em'
                }}>
                  <span className="material-icons notranslate" style={{ 
                    fontSize: '1.25rem', 
                    marginRight: '0.5rem',
                    color: '#667eea',
                    verticalAlign: 'middle'
                  }}>currency_rupee</span>
                  Price
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
              <input
                type="number"
                placeholder="Price"
                value={newProduct.price}
                onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value.replace(/[^\d]/g, '') })}
                    style={{
                      width: '100%',
                      padding: '1rem 1.25rem',
                      borderRadius: '16px',
                      border: '2px solid #e5e7eb',
                      fontSize: '1.1rem',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      color: '#374151',
                      fontWeight: '500',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                min="0"
              />
            </div>
              </div>

              {/* Contact Number Section */}
              <div className="form-group" style={{ marginBottom: '2rem' }}>
              <label style={{ 
                display: 'block', 
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#374151',
                  marginBottom: '0.75rem',
                  letterSpacing: '-0.025em'
                }}>
                  <span className="material-icons notranslate" style={{ 
                    fontSize: '1.25rem', 
                    marginRight: '0.5rem',
                    color: '#667eea',
                    verticalAlign: 'middle'
                  }}>phone</span>
                Contact Number
              </label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch', width: '100%' }}>
                <select
                  value={newProduct.countryCode || '+91'}
                  onChange={(e) => setNewProduct({ ...newProduct, countryCode: e.target.value })}
                  style={{
                      padding: '1rem 0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '16px',
                      fontSize: '1.1rem',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      color: '#374151',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      minWidth: '120px',
                      boxSizing: 'border-box',
                      outline: 'none',
                      height: 'auto',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+86">🇨🇳 +86</option>
                  <option value="+81">🇯🇵 +81</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+39">🇮🇹 +39</option>
                  <option value="+7">🇷🇺 +7</option>
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+82">🇰🇷 +82</option>
                  <option value="+65">🇸🇬 +65</option>
                  <option value="+60">🇲🇾 +60</option>
                  <option value="+66">🇹🇭 +66</option>
                  <option value="+84">🇻🇳 +84</option>
                  <option value="+62">🇮🇩 +62</option>
                  <option value="+63">🇵🇭 +63</option>
                  <option value="+27">🇿🇦 +27</option>
                  <option value="+20">🇪🇬 +20</option>
                </select>
                <input
                  type="tel"
                  placeholder="Enter 10-digit number"
                  value={newProduct.contact || ''}
                  onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) {
                      setNewProduct({ ...newProduct, contact: value });
                    }
                  }}
                  maxLength="10"
                  style={{
                    flex: '1 1 auto',
                    width: '100%',
                    minWidth: 0,
                      padding: '1rem 1.25rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '16px',
                      fontSize: '1.1rem',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      color: '#374151',
                      fontWeight: '500',
                      outline: 'none',
                      height: 'auto'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                  }}
                />
              </div>
              {newProduct.contact && newProduct.contact.length !== 10 && (
                <div style={{
                    color: '#ef4444',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span className="material-icons notranslate" style={{ fontSize: '1rem' }}>error</span>
                  Phone number must be exactly 10 digits
                </div>
              )}
            </div>

              {/* Product Description Section */}
              <div className="form-group" style={{ marginBottom: '2rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#374151',
                  marginBottom: '0.75rem',
                  letterSpacing: '-0.025em'
                }}>
                  <span className="material-icons notranslate" style={{ 
                    fontSize: '1.25rem', 
                    marginRight: '0.5rem',
                    color: '#667eea',
                    verticalAlign: 'middle'
                  }}>description</span>
                  Product Description
                </label>
            <textarea
              placeholder="Product Description"
              value={newProduct.description}
              onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
              style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '16px',
                    fontSize: '1.1rem',
                    minHeight: '120px',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    color: '#374151',
                    fontWeight: '500',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                />
              </div>
            </div>
            
            {/* Submit Button */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '2rem',
              paddingTop: '2rem',
              borderTop: '2px solid rgba(102, 126, 234, 0.1)'
            }}>
              <button
                onClick={handleAddProduct}
                style={{
                  padding: '1rem 3rem',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  boxShadow: '0 8px 15px -3px rgba(102, 126, 234, 0.4)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  minWidth: '200px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 12px 20px -5px rgba(102, 126, 234, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 15px -3px rgba(102, 126, 234, 0.4)';
                }}
              >
                <span className="material-icons notranslate" style={{ 
                  fontSize: '1.5rem',
                  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                }}>add_shopping_cart</span>
              Submit
            </button>
            </div>
          </div>
        </div>
      )}

        {showNewCategoryInput && (
            <div className="modal-overlay" style={{ 
              zIndex: 1001,
              animation: 'fadeIn 0.3s ease-out'
            }}>
              <div className="modal-content" style={{ 
                maxWidth: '650px', 
                width: '90vw',
                maxHeight: '85vh', 
                overflowY: 'auto',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: '20px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                position: 'relative',
                animation: 'slideIn 0.4s ease-out',
                backdropFilter: 'blur(10px)',
                transform: 'translateY(0)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                {/* Decorative background elements */}
                <div style={{
                  position: 'absolute',
                  top: '-2px',
                  left: '-2px',
                  right: '-2px',
                  bottom: '-2px',
                  background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '22px',
                  zIndex: -1,
                  opacity: 0.1
                }}></div>
                
                <div className="modal-header" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2rem',
                  paddingBottom: '1.5rem',
                  borderBottom: '2px solid rgba(99, 102, 241, 0.1)',
                  position: 'relative'
                }}>
                  <h2 style={{
                    fontSize: '1.75rem',
                    fontWeight: '800',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    letterSpacing: '-0.025em'
                  }}>
                    <span className="material-icons notranslate" style={{ 
                      color: '#667eea',
                      fontSize: '2rem',
                      filter: 'drop-shadow(0 2px 4px rgba(102, 126, 234, 0.3))'
                    }}>add_circle</span>
                    Add New Category
                  </h2>
                  <button
                    className="modal-close"
                    onClick={() => {
                setShowNewCategoryInput(false);
                setNewCategoryInput('');
                setSubcategoryInputs(['']);
                setShowSubcategorySection(false);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                      border: '2px solid rgba(99, 102, 241, 0.1)',
                      fontSize: '1.25rem',
                      cursor: 'pointer',
                      color: '#64748b',
                      padding: '0.75rem',
                      borderRadius: '12px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      width: '44px',
                      height: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                      e.target.style.color = 'white';
                      e.target.style.transform = 'scale(1.1) rotate(90deg)';
                      e.target.style.boxShadow = '0 8px 15px -3px rgba(239, 68, 68, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                      e.target.style.color = '#64748b';
                      e.target.style.transform = 'scale(1) rotate(0deg)';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <div className="modal-body" style={{ padding: '0 0.5rem' }}>
                  <div className="form-group" style={{ marginBottom: '2rem' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: '#374151',
                      marginBottom: '0.75rem',
                      letterSpacing: '-0.025em'
                    }}>
                      <span className="material-icons notranslate" style={{ 
                        fontSize: '1.25rem', 
                        marginRight: '0.5rem',
                        color: '#667eea',
                        verticalAlign: 'middle'
                      }}>category</span>
                      Category Name
                    </label>
                    <div style={{ position: 'relative' }}>
              <input
                placeholder="Enter new category name"
                value={newCategoryInput}
                onChange={(e) => setNewCategoryInput(e.target.value)}
                style={{
                  width: '100%',
                          padding: '1rem 1.25rem',
                          borderRadius: '16px',
                          border: '2px solid #e5e7eb',
                          fontSize: '1.1rem',
                          boxSizing: 'border-box',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          color: '#374151',
                          fontWeight: '500'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#667eea';
                          e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.1), 0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                          e.target.style.transform = 'translateY(0)';
                }}
                autoFocus
              />
                      <div style={{
                        position: 'absolute',
                        right: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af',
                        fontSize: '1.25rem'
                      }}>
                        <span className="material-icons notranslate">edit</span>
                      </div>
                    </div>
                  </div>
              
              {/* Subcategory Section */}
                  <div className="form-group">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                      marginBottom: '1rem'
                }}>
                  <label style={{ 
                        fontSize: '1rem', 
                        fontWeight: '700',
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span className="material-icons notranslate" style={{ 
                          fontSize: '1.25rem',
                          color: '#667eea'
                        }}>subdirectory_arrow_right</span>
                    Subcategories (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowSubcategorySection(!showSubcategorySection)}
                    style={{
                          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                          border: '2px solid rgba(102, 126, 234, 0.2)',
                          borderRadius: '12px',
                          padding: '0.75rem 1rem',
                          fontSize: '0.875rem',
                      cursor: 'pointer',
                          color: '#667eea',
                          fontWeight: '600',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                          e.target.style.color = 'white';
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 8px 15px -3px rgba(102, 126, 234, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)';
                          e.target.style.color = '#667eea';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                        }}
                      >
                        <span className="material-icons notranslate" style={{ 
                          fontSize: '1rem', 
                          marginRight: '0.5rem',
                          transition: 'transform 0.3s ease'
                        }}>
                          {showSubcategorySection ? 'expand_less' : 'expand_more'}
                        </span>
                        {showSubcategorySection ? 'Hide Subcategories' : 'Add Subcategories'}
                  </button>
                </div>
                
                {showSubcategorySection && (
                  <div style={{ 
                        border: '2px solid rgba(102, 126, 234, 0.1)', 
                        borderRadius: '16px', 
                        padding: '1.5rem',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        animation: 'slideDown 0.3s ease-out',
                        boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)'
                  }}>
                    {subcategoryInputs.map((subcategory, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                            gap: '0.75rem',
                            marginBottom: '1rem',
                            animation: 'fadeInUp 0.3s ease-out',
                            animationDelay: `${index * 0.1}s`
                      }}>
                        <input
                          placeholder={`Subcategory ${index + 1}`}
                          value={subcategory}
                          onChange={(e) => updateSubcategoryInput(index, e.target.value)}
                          style={{
                            flex: 1,
                                padding: '0.75rem 1rem',
                                borderRadius: '12px',
                                border: '2px solid #e5e7eb',
                                fontSize: '1rem',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: 'white',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#667eea';
                                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                          }}
                        />
                        {subcategoryInputs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSubcategoryInput(index)}
                            style={{
                                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: 'white',
                              border: 'none',
                                  borderRadius: '12px',
                                  padding: '0.75rem',
                              cursor: 'pointer',
                                  fontSize: '1rem',
                                  width: '44px',
                                  height: '44px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.1) rotate(90deg)';
                                  e.target.style.boxShadow = '0 8px 15px -3px rgba(239, 68, 68, 0.5)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1) rotate(0deg)';
                                  e.target.style.boxShadow = '0 4px 6px -1px rgba(239, 68, 68, 0.3)';
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={addSubcategoryInput}
                      style={{
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            color: '#667eea',
                            border: '2px dashed rgba(102, 126, 234, 0.4)',
                            borderRadius: '12px',
                            padding: '1rem',
                        cursor: 'pointer',
                            fontSize: '1rem',
                            width: '100%',
                            fontWeight: '600',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.borderColor = '#667eea';
                            e.target.style.color = '#667eea';
                            e.target.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)';
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 15px -3px rgba(102, 126, 234, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.borderColor = 'rgba(102, 126, 234, 0.4)';
                            e.target.style.color = '#667eea';
                            e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                          }}
                        >
                          <span className="material-icons notranslate" style={{ 
                            fontSize: '1.25rem', 
                            marginRight: '0.75rem',
                            transition: 'transform 0.3s ease'
                          }}>add_circle</span>
                          Add Another Subcategory
                    </button>
                  </div>
                )}
                  </div>
              </div>
              
                <div className="modal-footer" style={{
                display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '2.5rem',
                  paddingTop: '2rem',
                  borderTop: '2px solid rgba(102, 126, 234, 0.1)'
              }}>
                <button
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategoryInput('');
                    setSubcategoryInputs(['']);
                    setShowSubcategorySection(false);
                  }}
                  style={{
                      padding: '1rem 2rem',
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                      color: '#64748b',
                      border: '2px solid rgba(99, 102, 241, 0.1)',
                    cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '700',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                      e.target.style.color = 'white';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 15px -3px rgba(239, 68, 68, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                      e.target.style.color = '#64748b';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <span className="material-icons notranslate" style={{ fontSize: '1.25rem' }}>close</span>
                  Cancel
                </button>
                <button
                  onClick={handleAddNewCategory}
                  disabled={loading}
                  style={{
                      padding: '1rem 2rem',
                      borderRadius: '16px',
                      background: loading ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '700',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      boxShadow: loading ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : '0 8px 15px -3px rgba(16, 185, 129, 0.4)',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.background = 'linear-gradient(135deg, #059669 0%, #047857 100%)';
                        e.target.style.transform = 'translateY(-3px)';
                        e.target.style.boxShadow = '0 12px 20px -5px rgba(16, 185, 129, 0.6)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.target.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 8px 15px -3px rgba(16, 185, 129, 0.4)';
                      }
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="loading-spinner" style={{
                          width: '1.25rem',
                          height: '1.25rem',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Adding...
                      </>
                    ) : (
                      <>
                        <span className="material-icons notranslate" style={{ 
                          fontSize: '1.5rem',
                          filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                        }}>add_circle</span>
                        Add Category
                      </>
                    )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Category promotional images */}
        <div className="promotional-images" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '1.5rem',
          marginTop: '3rem',
         
        }}>
          {['cars', 'bikes', 'home-appliances', 'electronics', 'mobiles'].map(categoryId => (
            <div
              key={categoryId}
              className="promotional-image-card"
              onClick={() => setActiveCategory(categoryId)}
              style={{
                position: 'relative',
                cursor: 'pointer',
                borderRadius: 'var(--border-radius-xl)',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-md)',
                transition: 'all 0.3s ease',
                background: 'var(--white)',
                border: '1px solid var(--gray-200)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-4px)';
                e.target.style.boxShadow = 'var(--shadow-xl)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'var(--shadow-md)';
              }}
            >
              <img
              src={categoryProducts[categoryId] || defaultImages[categoryId]}
              alt={getCategoryDisplayName(categoryId)}
                style={{
                  width: '100%',
                  height: '300px',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease'
                }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x300/f3f4f6/6b7280?text=' + getCategoryDisplayName(categoryId);
                }}
              />
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
                color: 'white',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
                }}>
                  {getCategoryDisplayName(categoryId)}
                </h3>
                <p style={{
                  margin: '0.5rem 0 0 0',
                  fontSize: '1rem',
                  opacity: 0.9,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                }}>
                  Explore our {getCategoryDisplayName(categoryId).toLowerCase()} collection
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
  );
}

export default Home;

